import React, { useState, useEffect } from 'react';
import { 
  FaUsers, 
  FaUserPlus, 
  FaCheckCircle, 
  FaSearch,
  FaPlus,
  FaChartLine,
  FaEye,
  FaUserCheck,
  FaPhone,
  FaEnvelope,
  FaChurch
} from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";


import Header from '../../components/Header';
import DashboardCard from '../../components/DashboardCard';
import { useAuth } from '../../context/Auth';
import { toast } from 'react-toastify';
import { IoTrendingUp } from 'react-icons/io5';
import RecentVisitors from '../../components/Dashboard/RecentVisitors';


ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Quick Stats Component for Usher
const UsherStats = ({ data }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">Today's Attendance</p>
          <p className="text-3xl font-bold text-blue-600">{data?.today_attendance || 0}</p>
          <p className="text-sm text-green-600 flex items-center mt-1">
            <IoTrendingUp className="w-4 h-4 mr-1" />
            +{data?.attendance_growth?.growth_percentage || 0}% vs last week
          </p>
        </div>
        <div className="p-3 bg-blue-100 rounded-full">
          <FaUserCheck className="w-8 h-8 text-blue-600" />
        </div>
      </div>
    </div>

    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">New Visitors Today</p>
          <p className="text-3xl font-bold text-green-600">{data?.today_visitors || 0}</p>
          <p className="text-sm text-gray-500 mt-1">This service</p>
        </div>
        <div className="p-3 bg-green-100 rounded-full">
          <FaUserPlus className="w-8 h-8 text-green-600" />
        </div>
      </div>
    </div>

    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">Total Members</p>
          <p className="text-3xl font-bold text-purple-600">{data?.total_members || 0}</p>
          <p className="text-sm text-gray-500 mt-1">Active members</p>
        </div>
        <div className="p-3 bg-purple-100 rounded-full">
          <FaUsers className="w-8 h-8 text-purple-600" />
        </div>
      </div>
    </div>

    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">Attendance Rate</p>
          <p className="text-3xl font-bold text-orange-600">{data?.attendance_rate || 0}%</p>
          <p className="text-sm text-gray-500 mt-1">This month</p>
        </div>
        <div className="p-3 bg-orange-100 rounded-full">
          <FaEye className="w-8 h-8 text-orange-600" />
        </div>
      </div>
    </div>
  </div>
);

// Quick Attendance Marking Component
const QuickAttendanceMarking = ({ onMarkAttendance, recentMembers, loading }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMembers, setSelectedMembers] = useState(new Set());
  const [markingAttendance, setMarkingAttendance] = useState(false);

  const filteredMembers = recentMembers?.filter(member =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.member_id.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const toggleMemberAttendance = (memberId) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId);
    } else {
      newSelected.add(memberId);
    }
    setSelectedMembers(newSelected);
  };

  const handleMarkAttendance = async () => {
    setMarkingAttendance(true);
    try {
      await onMarkAttendance(Array.from(selectedMembers));
      setSelectedMembers(new Set());
      setSearchTerm('');
    } catch (error) {
      console.error('Error marking attendance:', error);
    } finally {
      setMarkingAttendance(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-800 flex items-center">
          <FaCheckCircle className="w-6 h-6 text-green-600 mr-2" />
          Quick Attendance Marking
        </h3>
        {selectedMembers.size > 0 && (
          <button
            onClick={handleMarkAttendance}
            disabled={markingAttendance}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <FaCheckCircle className="w-4 h-4 mr-2" />
            {markingAttendance ? 'Marking...' : `Mark ${selectedMembers.size} Present`}
          </button>
        )}
      </div>

      <div className="relative mb-4">
        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder="Search members by name or ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="max-h-64 overflow-y-auto space-y-2">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading members...</p>
          </div>
        ) : filteredMembers.length > 0 ? (
          filteredMembers.map((member) => (
            <div
              key={member.id}
              onClick={() => toggleMemberAttendance(member.id)}
              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                selectedMembers.has(member.id)
                  ? 'bg-green-50 border-2 border-green-200 shadow-md'
                  : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <FaUsers className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-800">{member.name}</p>
                  <p className="text-sm text-gray-500">ID: {member.member_id}</p>
                </div>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                selectedMembers.has(member.id)
                  ? 'bg-green-600 border-green-600'
                  : 'border-gray-300'
              }`}>
                {selectedMembers.has(member.id) && (
                  <FaCheckCircle className="w-4 h-4 text-white" />
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-gray-500">
            <FaUsers className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No members found</p>
            {searchTerm && (
              <p className="text-sm">Try adjusting your search term</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Visitor Registration Component
const VisitorRegistration = ({ onAddVisitor }) => {
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name:'',
    phone_number: '',
    email: '',
    address: ''
    
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onAddVisitor(formData);
      setFormData({
        name: '',
        phone: '',
        email: '',
        address: '',
        how_heard: '',
        first_time: true
      });
      setShowForm(false);
    } catch (error) {
      console.error('Error adding visitor:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-800 flex items-center">
          <FaUserPlus className="w-6 h-6 text-blue-600 mr-2" />
          Visitor Registration
        </h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <FaPlus className="w-4 h-4 mr-2" />
          {showForm ? 'Cancel' : 'Add Visitor'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                name="first_name"
                required
                value={formData.first_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter visitor's first name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                name="last_name"
                required
                value={formData.last_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter visitor's last name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Phone number"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Email address"
              />
            </div>
          </div>


          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Full address"
            />
          </div>


          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <FaUserPlus className="w-4 h-4 mr-2" />
              {submitting ? 'Registering...' : 'Register Visitor'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

// Attendance Trends Chart
const AttendanceTrends = ({ data }) => {
  const chartData = {
    labels: data?.attendance_weeks || ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5', 'Week 6'],
    datasets: [
      {
        label: 'Members',
        data: data?.weekly_members || [65, 72, 58, 75, 68, 82],
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1,
      },
      {
        label: 'Visitors',
        data: data?.weekly_visitors || [15, 20, 12, 18, 16, 22],
        backgroundColor: 'rgba(34, 197, 94, 0.8)',
        borderColor: 'rgba(34, 197, 94, 1)',
        borderWidth: 1,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Weekly Attendance Trends'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 10
        }
      }
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
      <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
        <FaChartLine className="w-6 h-6 text-blue-600 mr-2" />
        Attendance Trends
      </h3>
      <div style={{ height: '300px' }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};



// Today's Service Summary
const TodayServiceSummary = ({ data }) => (
  <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
    <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
      <FaChurch className="w-6 h-6 text-purple-600 mr-2" />
      Today's Service Summary
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
        <p className="text-3xl font-bold text-blue-600">{data?.total_present || 0}</p>
        <p className="text-sm text-gray-600">Total Present</p>
        <p className="text-xs text-gray-500 mt-1">Members + Visitors</p>
      </div>
      <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl">
        <p className="text-3xl font-bold text-green-600">{data?.members_present || 0}</p>
        <p className="text-sm text-gray-600">Members Present</p>
        <p className="text-xs text-gray-500 mt-1">Regular attendees</p>
      </div>
      <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl">
        <p className="text-3xl font-bold text-orange-600">{data?.visitors_present || 0}</p>
        <p className="text-sm text-gray-600">Visitors Present</p>
        <p className="text-xs text-gray-500 mt-1">New faces today</p>
      </div>
    </div>
  </div>
);

// Main Usher Dashboard Component
const UsherDashboard = () => {
  const { requestWithAuth} = useAuth();
  const [loading, setLoading] = useState(false);
  const [gettingData, setGettingData] = useState(false)
  const [dashboardData, setDashboardData] = useState({});

  const [recentMembers, setRecentMembers] = useState([
    { id: 1, name: 'John Smith', member_id: 'M001' },
    { id: 2, name: 'Mary Johnson', member_id: 'M002' },
    { id: 3, name: 'David Brown', member_id: 'M003' },
    { id: 4, name: 'Sarah Wilson', member_id: 'M004' },
    { id: 5, name: 'Michael Davis', member_id: 'M005' },
    { id: 6, name: 'Linda Garcia', member_id: 'M006' },
    { id: 7, name: 'Robert Miller', member_id: 'M007' },
    { id: 8, name: 'Jennifer Martinez', member_id: 'M008' }
  ]);

  const [recentVisitors, setRecentVisitors] = useState([]);

  // Mock function to mark attendance
  const handleMarkAttendance = async (memberIds) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update dashboard data
      setDashboardData(prev => ({
        ...prev,
        today_attendance: prev.today_attendance + memberIds.length,
        members_present: prev.members_present + memberIds.length,
        total_present: prev.total_present + memberIds.length
      }));

      console.log('Marked attendance for:', memberIds);
    } catch (error) {
      console.error('Error marking attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mock function to add visitor
  const handleAddVisitor = async (visitorData) => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newVisitor = {
        ...visitorData,
        visit_date: 'Today'
      };

      setRecentVisitors(prev => [newVisitor, ...prev]);
      
      // Update dashboard data
      setDashboardData(prev => ({
        ...prev,
        today_visitors: prev.today_visitors + 1,
        visitors_present: prev.visitors_present + 1,
        total_present: prev.total_present + 1
      }));

      console.log('Added visitor:', newVisitor);
    } catch (error) {
      console.error('Error adding visitor:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDashboardData = async () => {
    setGettingData(true);
    try {
      const response = await requestWithAuth("/accounts/usher/dashboard/", "GET");
      setDashboardData(response?.data);
    } catch (error) {
      toast.error("An error occurred while getting data");
    } finally {
      setGettingData(false);
    }
  };

  const getRecentVisitors = async () => {
    setLoading(true);
    try {
      const response = await requestWithAuth("/members/visitors/recent/", "GET");
      setRecentVisitors(response?.data);
    } catch (error) {
      toast.error("An error occurred while getting data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDashboardData();
    getRecentVisitors();
    
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="Usher Dashboard" />
      
      {/* Main Dashboard Cards */}
      <div className="grid lg:grid-cols-3 lg:gap-8 mb-8">
        <DashboardCard
          title="Today's Attendance"
          subtitle="Members Present"
          value={dashboardData?.today_attendance || 0}
          icon={FaUserCheck}
          trendValue="+5%"
          color="blue"
        />

        <DashboardCard
          title="New Visitors"
          subtitle="Today's Service"
          value={dashboardData?.today_visitors || 0}
          icon={FaUserPlus}
          trendValue="+8%"
          color="green"
        />

        <DashboardCard
          title="Total Members"
          subtitle="Active"
          value={dashboardData?.total_members || 0}
          icon={FaUsers}
          trendValue="+2%"
          color="purple"
        />
      </div>

      {/* Quick Stats Overview */}
      <UsherStats data={dashboardData} />

      {/* Today's Service Summary */}
      {/* <TodayServiceSummary data={dashboardData} />

      
      <div className="grid lg:grid-cols-2 lg:gap-8 mb-8">
        <QuickAttendanceMarking 
          onMarkAttendance={handleMarkAttendance}
          recentMembers={recentMembers}
          loading={loading}
        />
        <VisitorRegistration onAddVisitor={handleAddVisitor} />
      </div> */}

      {/* Charts and Lists Section */}
      <div className="grid lg:grid-cols-2 lg:gap-8">
        <AttendanceTrends data={dashboardData?.attendance_data} />
        <RecentVisitors visitors={recentVisitors} loading={loading} />
      </div>
    </div>
  );
};

export default UsherDashboard;